import type { Signal } from "@preact-signals/safe-react";
import { getYRecords } from "@src/components/board/board-sync-loader/get-y-records";
import type { TLRecord } from "tldraw";
import type { Doc, Transaction } from "yjs";
import type { SyncStatus } from "./auth-retry";
import type { IndexeddbPersistence } from "./indexeddb-persistence";
import { isLocalFirstYjsEnabled } from "./local-first-settings";

export function hasRenderableBoardData(document: Doc): boolean {
	const recordsArray = document.getArray<{ key: string; val?: TLRecord }>(
		"records",
	);

	return recordsArray.toArray().some((entry) => {
		return entry?.val?.typeName === "page" || entry?.val?.typeName === "shape";
	});
}

export function hasLocalDocumentContent(document: Doc): boolean {
	if (hasRenderableBoardData(document)) {
		return true;
	}

	if (document.getXmlFragment("document-store").length > 0) {
		return true;
	}

	if (document.getMap("meta").size > 0) {
		return true;
	}

	return false;
}

/**
 * Hocuspocus may stream board records before onSynced (common for share-key
 * guests). Promote pending → synced-remote once renderable data is present.
 * When local-first Yjs is enabled, records originating from IndexeddbPersistence
 * promote pending → synced-local instead so remote schema migrations wait for
 * authoritative server sync, and subsequent non-local transactions promote
 * pending | synced-local → synced-remote.
 */
export function watchDocumentReady(
	document: Doc,
	syncedStateSignal: Signal<SyncStatus>,
	localProvider?: IndexeddbPersistence,
) {
	const records = getYRecords(document);

	const markSyncedWhenDataArrives = (
		_changes?: unknown,
		transaction?: Transaction,
	) => {
		if (!hasRenderableBoardData(document)) {
			return;
		}

		if (localProvider && isLocalFirstYjsEnabled()) {
			if (transaction?.origin === localProvider) {
				if (syncedStateSignal.value === "pending") {
					syncedStateSignal.value = "synced-local";
				}
				return;
			}

			if (
				transaction &&
				!transaction.local &&
				syncedStateSignal.value === "synced-local"
			) {
				syncedStateSignal.value = "synced-remote";
				return;
			}
		}

		if (syncedStateSignal.value === "pending") {
			syncedStateSignal.value = "synced-remote";
		}
	};

	markSyncedWhenDataArrives();
	records.on("change", markSyncedWhenDataArrives);

	return () => {
		records.off("change", markSyncedWhenDataArrives);
	};
}
